import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import { Universe } from '@smart-tv/typings/content';
import getContentUniverses from '@smart-tv/utils/kmm/getContentUniverses';

import { HEADER_EXPLORE } from '../header/header-helpers';
import { useNavigateToHeaderDestination } from '../header/header-navigation';
import { CmdError, BaseError, networkError } from '@smart-tv/contexts/error-context/error-context';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  headerActiveAtom,
  exploreLastSelectedTitleAtom,
  topLevelPageReadyAtom
} from '@smart-tv/atoms';
import {
  retrievePersistentData,
  storePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

const useExplorePicker = () => {
  const [navigationContent, setNavigationContent] = useState<Universe[]>();
  const [error, setError] = useState<CmdError | BaseError | undefined>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { checkAndUpdateNetwork, setIsOffline, setIsNetworkDialogSuppressed } = useNetwork();
  const [selectedUniverse, setSelectedUniverse] = useState<{
    id: number | undefined;
    title: string | undefined;
  }>({
    id: undefined,
    title: undefined
  });

  const activeItemInHeader = useAtomValue(headerActiveAtom);
  const navigateToHeaderDestination = useNavigateToHeaderDestination();
  const { ref, focusKey } = useFocusable();
  const [lastSelectedTitle, setLastSelectedTitle] = useAtom(exploreLastSelectedTitleAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);

  const fetchFromAPI = useCallback(async () => {
    try {
      const responseNavigationContent = await getContentUniverses();
      storePersistentData('navigationContent', responseNavigationContent, false);
      storePersistentData('navigationContentTimestamp', Date.now().toString(), false);
      setNavigationContent(responseNavigationContent);
      setError(undefined);
    } catch (error) {
      if (error === networkError || (error as BaseError).offline) {
        setError(networkError);
      }
    }
  }, []);

  const fetchNavigationContent = useCallback(async () => {
    const storedNavigationContent = retrievePersistentData<Universe[]>('navigationContent');
    const storedTimestamp = retrievePersistentData<string>('navigationContentTimestamp');
    const oneHour = 60 * 60 * 1000;

    if (
      storedNavigationContent &&
      storedTimestamp &&
      Date.now() - parseInt(storedTimestamp, 10) < oneHour
    ) {
      setNavigationContent(storedNavigationContent);
    } else {
      await fetchFromAPI();
    }
  }, []);

  const retryFetchNavigationContent = useCallback(async () => {
    await fetchFromAPI();
  }, []);

  useEffect(() => {
    fetchNavigationContent();
  }, [fetchNavigationContent]);

  useEffect(() => {
    if (navigationContent?.length || error) setTopLevelPageReady(true);
  }, [navigationContent, error, setTopLevelPageReady]);

  useEffect(() => {
    if (state?.headerPreview) {
      return;
    }

    if (state?.focusFirst && navigationContent) {
      setFocus(`${navigationContent[0].title}-focus-key`);
    } else if (state?.universeTitle) {
      setFocus(`${state.universeTitle}-focus-key`);
    } else if (lastSelectedTitle) {
      setFocus(`${lastSelectedTitle}-focus-key`);
    } else if (navigationContent) {
      setFocus(`${navigationContent[0].title}-focus-key`);
    }
  }, [navigationContent, state, lastSelectedTitle]);


  const handleHeaderNavigationClick = useCallback(async () => {
    setIsOffline(false);

    const hasNetwork = await checkAndUpdateNetwork();
    if (!hasNetwork) {
      return;
    }

    navigateToHeaderDestination(activeItemInHeader);
  }, [checkAndUpdateNetwork, activeItemInHeader, navigateToHeaderDestination, setIsOffline]);

  const handleUniverseNavigationClick = useCallback(
    async (id: number | undefined, title: string | undefined) => {
      setIsNetworkDialogSuppressed(false);
      setIsOffline(false);

      const hasNetwork = await checkAndUpdateNetwork();
      if (!hasNetwork) {
        setSelectedUniverse({ id, title });
        return;
      }
      setLastSelectedTitle(title);
      navigate(`universe/${title}/${id}`);
    },
    [
      navigate,
      checkAndUpdateNetwork,
      setSelectedUniverse,
      setLastSelectedTitle,
      setIsOffline,
      setIsNetworkDialogSuppressed
    ]
  );

  const handleNavigationClick = useCallback(
    async (id: number | undefined, title: string | undefined) => {
      if (id && title) {
        handleUniverseNavigationClick(id, title);
      } else {
        handleHeaderNavigationClick();
      }
    },
    [handleUniverseNavigationClick, handleHeaderNavigationClick]
  );

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (isBackKey(event)) {
      setFocus(HEADER_EXPLORE);
    }
  }, []);

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return {
    navigationContent,
    handleNavigationClick,
    ref,
    focusKey,
    error,
    retryFetchNavigationContent,
    selectedUniverse
  };
};

export default useExplorePicker;
